import logger from '3-foundation/Logging/logger';
import { useSiteContext } from '3-foundation/Site/SiteContext';
import { ErrorBoundary } from 'react-error-boundary';
import { capitalizeFirstLetter } from '3-foundation/Helpers/capitalizeFirstLetter';
import { NODE_ENV } from '../../3-foundation/_constants';
import dynamic from 'next/dynamic';

// Dynamic import can't be used inside of React rendering as it needs to be marked in the top level of the module for preloading to work, similar to React.lazy.
// https://nextjs.org/docs/pages/building-your-application/optimizing/lazy-loading#examples

// Don't get tempted to dynamically generate the imports, eg. with a forEach loop and template literals or similar.
// Tried and tested, it just wont work. It fails silently by disabling SSR and initial hydration of data!
// From the documentation: In import('path/to/component'), the path must be explicitly written. It can't be a template string nor a variable.

// Add a new module by adding it to the imports
const modules = {
	Accordion: dynamic(() => import('../../2-feature/Content/Accordion').then((module) => module['Accordion'])),
	Anchor: dynamic(() => import('../../2-feature/Content/Anchor').then((module) => module['Anchor'])),
	ArticleMedia: dynamic(() => import('../../2-feature/Content/ArticleMedia').then((module) => module['ArticleMedia'])),
	ArticleText: dynamic(() => import('../../2-feature/Content/ArticleText').then((module) => module['ArticleText'])),
	BookmarkMenu: dynamic(() => import('../../2-feature/Bookmark/BookmarkMenu').then((module) => module['BookmarkMenu'])),
	Breadcrumb: dynamic(() => import('../../2-feature/Navgation/Breadcrumb').then((module) => module['Breadcrumb'])),
	ContactList: dynamic(() => import('../../2-feature/Content/ContactList').then((module) => module['ContactList'])),
	ContentCard: dynamic(() => import('../../2-feature/Content/ContentCard').then((module) => module['ContentCard'])),
	ContentCardList: dynamic(() => import('../../2-feature/Content/ContentCardList').then((module) => module['ContentCardList'])),
	ContentSection: dynamic(() => import('../../3-foundation/ContentSection/ContentSection').then((module) => module['ContentSection'])),
	EventList: dynamic(() => import('../../2-feature/Content/EventList').then((module) => module['EventList'])),
	FooterCard: dynamic(() => import('../Footer/FooterCard').then((module) => module['FooterCard'])),
	HeaderCard: dynamic(() => import('../../2-feature/Content/HeaderCard').then((module) => module['HeaderCard'])),
	HeroCard: dynamic(() => import('../../2-feature/Content/HeroCard').then((module) => module['HeroCard'])),
	HeroCardSearch: dynamic(() => import('../../2-feature/Content/HeroCardSearch').then((module) => module['HeroCardSearch'])),
	LinkList: dynamic(() => import('../../2-feature/Content/LinkList').then((module) => module['LinkList'])),
	LinkListGrid: dynamic(() => import('../../2-feature/Content/LinkListGrid').then((module) => module['LinkListGrid'])),
	LocalNavigation: dynamic(() => import('../../2-feature/Navgation/LocalNavigation').then((module) => module['LocalNavigation'])),
	Map: dynamic(() => import('../../2-feature/Map/Map').then((module) => module['Map'])),
	NewsList: dynamic(() => import('../../2-feature/News/NewsList').then((module) => module['NewsList'])),
	OrganisationList: dynamic(() => import('../../2-feature/Content/OrganisationList').then((module) => module['OrganisationList'])),
	ScriptModule: dynamic(() => import('../../2-feature/Content/ScriptModule').then((module) => module['ScriptModule'])),
	SharedContentModule: dynamic(() => import('../../2-feature/Content/SharedContentModule').then((module) => module['SharedContentModule'])),
	Slider: dynamic(() => import('../../2-feature/Content/Slider').then((module) => module['Slider'])),
	Tabs: dynamic(() => import('../../3-foundation/Tabs/Tabs').then((module) => module['Tabs'])),
	SubjectTags: dynamic(() => import('../../2-feature/Navgation/TagsList').then((module) => module['TagsList'])),
	VideoCard: dynamic(() => import('../../2-feature/Content/VideoCard').then((module) => module['VideoCard'])),
};

export interface DynamicModuleFeatureProps {
	module: Content.ContentModule | Content.ContentSection | Content.Tabs;
}

export const DynamicModule = ({ module }: DynamicModuleFeatureProps) => {
	const production = NODE_ENV === 'production';
	const { host: siteHost } = useSiteContext();

	const { documentType } = module;
	const moduleName = `${capitalizeFirstLetter(documentType)}`;
	const Module = modules[moduleName];

	const message = `Component [${documentType}] is missing a React Component. Add missing export for '${moduleName}'`;

	if (!Module && !production) {
		return <div>{message}</div>;
	}

	if (!Module && production) {
		logger.error(message, { siteHost });
		return null;
	}

	const errorHandler = (error: Error, info: { componentStack: string }) => {
		logger.error(`Error with component [${documentType}]: ${error}. ${info.componentStack}`, { siteHost });
	};

	return (
		<ErrorBoundary FallbackComponent={() => null} onError={errorHandler}>
			<Module {...module} />
		</ErrorBoundary>
	);
};
